import type { NotificationList as NotificationModel } from "../generated-axios-client";
import { formatComponentLabel } from "../../common/utils/componentLabel";

export type NotificationGroupKind =
  | "ticket"
  | "turbine"
  | "location"
  | "client"
  | "project"
  | "material"
  | "component"
  | "other";

export interface NotificationGroup {
  key: string;
  kind: NotificationGroupKind;
  entityId: string;
  label: string;
  events: NotificationModel[];
  unreadCount: number;
  latestCreatedAt: string;
}

function labelForTicket(ticket: NonNullable<NotificationModel["ticket"]>): string {
  if (ticket.ticket_number != null) {
    return ticket.name
      ? `WO #${ticket.ticket_number} · ${ticket.name}`
      : `WO #${ticket.ticket_number}`;
  }
  return ticket.name ?? "Work Order";
}

export function groupNotifications(
  notifications: NotificationModel[]
): NotificationGroup[] {
  const groups = new Map<string, NotificationGroup>();

  for (const n of notifications) {
    let kind: NotificationGroupKind;
    let entityId: string;
    let label: string;

    if (n.ticket?.id) {
      kind = "ticket";
      entityId = n.ticket.id;
      label = labelForTicket(n.ticket);
    } else if (n.turbine?.id) {
      kind = "turbine";
      entityId = n.turbine.id;
      label = n.turbine.serial_number ?? "Turbine";
    } else if (n.location?.id) {
      kind = "location";
      entityId = n.location.id;
      label = n.location.name ?? "Site";
    } else if (n.client?.id) {
      kind = "client";
      entityId = n.client.id;
      label = n.client.legal_name ?? "Client";
    } else if (n.project?.id) {
      kind = "project";
      entityId = n.project.id;
      label = n.project.name ?? "Project";
    } else if (n.material?.id) {
      kind = "material";
      entityId = n.material.id;
      label = n.material.name ?? "Supply";
    } else if (n.component?.id) {
      kind = "component";
      entityId = n.component.id;
      label = formatComponentLabel(n.component);
    } else {
      // No related entity on the notification itself - keep each of these standalone
      // rather than merging unrelated events into a single misleading "other" bucket.
      kind = "other";
      entityId = n.id ?? crypto.randomUUID();
      label = "Other activity";
    }

    const key = `${kind}:${entityId}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        kind,
        entityId,
        label,
        events: [],
        unreadCount: 0,
        latestCreatedAt: n.created_at ?? "",
      };
      groups.set(key, group);
    }

    group.events.push(n);
    if (!n.read_at) group.unreadCount += 1;
    if ((n.created_at ?? "") > group.latestCreatedAt) {
      group.latestCreatedAt = n.created_at ?? group.latestCreatedAt;
    }
  }

  const result = Array.from(groups.values());
  for (const group of result) {
    group.events.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
  }
  result.sort((a, b) => b.latestCreatedAt.localeCompare(a.latestCreatedAt));
  return result;
}

export function formatSenderName(sender: NotificationModel["sender"]): string {
  if (!sender) return "Unknown";
  const fullName = [sender.first_name, sender.last_name].filter(Boolean).join(" ").trim();
  return fullName || sender.username || "Unknown";
}
